import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, DatePicker, Divider, Flex, Form, Modal, Popconfirm, Select, Table, Tag, Tooltip, Typography } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'
import { fx } from '../../../../libs'
import { showNotification } from '../../../../features/notifications'

type Exclusion = { id: number; userId: string; username: string; excludedFrom: string | null }
type Manager = { id: string; username: string }

const listExclusions = fx<void, { items: Exclusion[] }>({ method: 'GET', url: '/statistics/fin-kpi-deposit-exclusions' })
const listManagers = fx<void, { items: Manager[] }>({ method: 'GET', url: '/user/by_role/finance_manager' })
const createExclusion = fx<{ userId: string; excludedFrom: string | null }, void>({ method: 'POST', url: '/statistics/fin-kpi-deposit-exclusions' })
const updateExclusion = fx<{ excludedFrom: string | null }, void>({ method: 'PUT', url: '/statistics/fin-kpi-deposit-exclusions/{id}' })
const deleteExclusion = fx<void, void>({ method: 'DELETE', url: '/statistics/fin-kpi-deposit-exclusions/{id}' })

export const DepositExclusions = () => {
  const [items, setItems] = useState<Exclusion[]>([])
  const [managers, setManagers] = useState<Manager[]>([])
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Exclusion | null>(null)
  const [form] = Form.useForm<{ userId: string; excludedFrom?: Dayjs }>()
  const reportError = (error: any) => showNotification({
    type: 'error',
    message: 'Request error',
    description: error?.body?.message ?? 'Не удалось выполнить запрос. Попробуйте ещё раз.',
  })
  const load = () => listExclusions({})
    .then((r) => setItems(r.body.items))
    .catch(reportError)

  useEffect(() => {
    load()
    listManagers({}).then((r) => setManagers(r.body.items)).catch(reportError)
  }, [])

  const submit = async () => {
    const value = await form.validateFields()
    const excludedFrom = value.excludedFrom?.format('YYYY-MM-DD') ?? null
    try {
      if (editing) await updateExclusion({ url: `/statistics/fin-kpi-deposit-exclusions/${editing.id}`, body: { excludedFrom } })
      else await createExclusion({ body: { userId: value.userId, excludedFrom } })
      setOpen(false); setEditing(null); form.resetFields(); load()
    } catch (error) {
      reportError(error)
    }
  }

  return <>
    <Divider orientation="left">Исключение депозитов менеджеров из KPI</Divider>
    <Flex justify="space-between" style={{ marginBottom: 12 }}>
      <Typography.Text type="secondary">Исключение действует с указанной даты включительно; пустая дата — за всё время.</Typography.Text>
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setEditing(null); form.resetFields(); setOpen(true) }}>Добавить</Button>
    </Flex>
    <Table size="small" rowKey="id" dataSource={items} pagination={false} columns={[
      { title: 'Менеджер', dataIndex: 'username' },
      { title: 'Исключать с', render: (_, r) => r.excludedFrom ? dayjs(r.excludedFrom).format('DD.MM.YYYY') : <Tag>За всё время</Tag> },
      { title: 'Действия', width: 120, render: (_, r) => <Flex gap={4}><Tooltip title="Изменить"><Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); form.setFieldsValue({ userId: r.userId, excludedFrom: r.excludedFrom ? dayjs(r.excludedFrom) : undefined }); setOpen(true) }} /></Tooltip><Popconfirm title="Удалить исключение?" onConfirm={() => deleteExclusion({ url: `/statistics/fin-kpi-deposit-exclusions/${r.id}` }).then(load).catch(reportError)}><Button danger size="small" icon={<DeleteOutlined />} /></Popconfirm></Flex> },
    ]} />
    <Modal title={editing ? 'Изменить исключение депозитов' : 'Добавить исключение депозитов'} open={open} onOk={submit} onCancel={() => { setOpen(false); setEditing(null) }} destroyOnClose>
      <Form form={form} layout="vertical"><Form.Item name="userId" label="Менеджер" rules={[{ required: true }]}><Select disabled={!!editing} options={managers.map((m) => ({ value: m.id, label: m.username }))} /></Form.Item><Form.Item name="excludedFrom" label="Дата начала исключения"><DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} placeholder="За всё время" /></Form.Item></Form>
    </Modal>
  </>
}
